import { groupTint, TRADER_TAG_INFO, type TraderTag } from '@pleco/shared';
import { shortAddr } from '../../lib/fmt.js';
import { groupById, groupLabel, useTerminal } from '../../store/terminalStore.js';

/** Order tags so the most incriminating ones show first. */
const TAG_ORDER: TraderTag[] = [
  'dev',
  'insider',
  'launch_bundle',
  'farm_wallet',
  'gas_sponsored',
  'token_recipient',
  'shared_funder',
  'coordinated_sell',
  'batch_funded',
  'sniper',
  'repeat_buyer',
  'big_buyer',
  'fresh_wallet',
  'cex_funded',
  'exited',
];

export function TagChips({ tags, max = 3, full = false }: { tags?: TraderTag[]; max?: number; full?: boolean }) {
  if (!tags?.length) return null;
  const sorted = [...tags].sort((a, b) => TAG_ORDER.indexOf(a) - TAG_ORDER.indexOf(b));
  const shown = full ? sorted : sorted.slice(0, max);
  return (
    <span className="ttags">
      {shown.map((t) => {
        const i = TRADER_TAG_INFO[t];
        return (
          <span key={t} className={`ttag ${i.tone}`} title={`${i.label} — ${i.help}`}>
            {full ? i.label : i.short}
          </span>
        );
      })}
      {!full && sorted.length > max && <span className="ttag muted">+{sorted.length - max}</span>}
    </span>
  );
}

/** Small coloured "G2" pill for a group id; click selects the group. */
export function GroupPill({ id, onClick }: { id?: string; onClick?: () => void }) {
  const analysis = useTerminal((s) => s.analysis);
  const g = groupById(analysis, id);
  if (!g) return null;
  const c = groupTint(g);
  return (
    <button
      type="button"
      className="gpill"
      style={{ color: c, borderColor: `${c}66`, background: `${c}1a` }}
      title={`${g.name} · ${g.wallets.length} wallets`}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      {groupLabel(g)}
    </button>
  );
}

/** Wallet address cell: group dot, short address, tags; opens the wallet drawer. */
export function WalletCell({ address, tags, groupId, label }: { address: string; tags?: TraderTag[]; groupId?: string; label?: string }) {
  const { openWallet, selectGroup, setHoverWallet, analysis } = useTerminal();
  const g = groupById(analysis, groupId);
  return (
    <span
      className="wcell"
      onMouseEnter={() => setHoverWallet(address)}
      onMouseLeave={() => setHoverWallet(undefined)}
      onClick={(e) => {
        e.stopPropagation();
        openWallet(address);
      }}
      title={`${address} — open wallet & fund flow`}
    >
      <i className="dot" style={{ background: g ? groupTint(g) : 'var(--border-strong)' }} />
      <span className="mono addr">{label ?? shortAddr(address)}</span>
      {g && <GroupPill id={groupId} onClick={() => selectGroup(groupId)} />}
      <TagChips tags={tags} max={2} />
    </span>
  );
}
